import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from "react-router-dom";
import PlantillaScreen from '../../../helpers/PlantillaScreen';
import { LoaderScreen } from '../../../components/LoaderScreen';
import request from '../../../../Helpers/API';
import Swal from 'sweetalert2';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { ButtonBack } from '../Util/ButtonBack';

const itemVacio = (orden) => ({
    _key: Math.random().toString(36).slice(2),
    idSubActividad: 0,
    nombreSubActividad: "",
    orden,
    estado: true,
});

// Configura el checklist (subactividades) que se clona automáticamente a
// cada ticket que se cree para esta actividad. Si una actividad no tiene
// ningún renglón aquí, sus tickets se comportan exactamente igual que hoy
// (sin checklist, sin bloqueo al Finalizar). Mismo patrón visual que
// CatSalaCamposExtraScreen.js -- reutiliza las clases csce-* ya existentes.
export const CatActividadSubActividadesScreen = () => {

    const history = useHistory();
    const { id } = useParams();

    const [nombreActividad, setNombreActividad] = useState("");
    const [items, setItems] = useState([]);
    const [loader, setLoader] = useState({ mostrar: false });
    const [guardando, setGuardando] = useState(false);

    const cargar = () => {
        if (!id) return;
        setLoader({ mostrar: true });

        Promise.all([
            request.get(`Actividad/Obtener/${id}`).catch(() => null),
            request.get(`SubActividad/Listado/${id}`).catch(() => []),
        ])
            .then(([actividad, listado]) => {
                setNombreActividad(actividad?.nombreactividad || "");
                const data = listado?.data ?? listado ?? [];
                setItems(
                    (data || []).map((s, index) => ({
                        _key: Math.random().toString(36).slice(2),
                        idSubActividad: s.idSubActividad,
                        nombreSubActividad: s.nombreSubActividad || "",
                        orden: s.orden ?? index,
                        estado: s.estado,
                    }))
                );
            })
            .catch(() => {
                Swal.fire("ERROR", "No se pudo cargar el checklist de la actividad", "error");
            })
            .finally(() => setLoader({ mostrar: false }));
    };

    useEffect(cargar, [id]);

    const onAgregarItem = () => {
        setItems((prev) => [...prev, itemVacio(prev.length)]);
    };

    const onQuitarItem = (item) => {
        if (!item.idSubActividad) {
            setItems((prev) => prev.filter((i) => i._key !== item._key));
            return;
        }

        Swal.fire({
            title: "¿Eliminar este paso del checklist?",
            text: "Ya no se incluirá en los tickets nuevos de esta actividad.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Eliminar",
            cancelButtonText: "Cancelar",
        }).then((res) => {
            if (!res.isConfirmed) return;

            request
                .post("SubActividad/EliminarPorID", item.idSubActividad)
                .then(() => {
                    setItems((prev) => prev.filter((i) => i._key !== item._key));
                })
                .catch((err) => {
                    // eslint-disable-next-line no-console
                    console.error("SubActividad/EliminarPorID error:", err);
                    Swal.fire("ERROR", err?.mensaje || err?.message || "No se pudo eliminar el paso", "error");
                });
        });
    };

    const onCambiarNombre = (key, nombreSubActividad) => {
        setItems((prev) => prev.map((i) => (i._key === key ? { ...i, nombreSubActividad } : i)));
    };

    const hayCambiosInvalidos = useMemo(
        () => items.some((i) => !i.nombreSubActividad.trim()),
        [items]
    );

    const onGuardar = async () => {
        if (hayCambiosInvalidos) {
            Swal.fire("Aviso", "Cada paso del checklist necesita un nombre", "warning");
            return;
        }

        try {
            setGuardando(true);

            for (let index = 0; index < items.length; index++) {
                const item = items[index];
                await request.post("SubActividad/Guardar", {
                    idSubActividad: item.idSubActividad,
                    idActividad: Number(id),
                    nombreSubActividad: item.nombreSubActividad.trim(),
                    orden: index,
                    estado: true,
                });
            }

            Swal.fire("Correcto", "Checklist guardado correctamente", "success");
            cargar();
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error("SubActividad/Guardar error:", err);
            Swal.fire("Error", err?.mensaje || err?.message || "No se pudo guardar el checklist", "error");
        } finally {
            setGuardando(false);
        }
    };

    return (
        <PlantillaScreen
            titulo={"CHECKLIST DE LA ACTIVIDAD"}
            className_main={1}
            className={"seguimiento-actividades-contenedor"}
        >
            <LoaderScreen loader={loader} />

            <div className="csce-contenido">

                <div className="csce-intro">
                    Define aquí los pasos (checklist) que debe completar el responsable en cada
                    ticket de <strong>{nombreActividad || `la actividad #${id}`}</strong>. Un ticket
                    de esta actividad no podrá marcarse como Finalizado hasta que todos los pasos
                    estén completados. Si no agregas ningún paso, el ticket funciona igual que hoy.
                </div>

                {items.length === 0 && (
                    <div className="csce-vacio">Esta actividad no tiene checklist configurado todavía.</div>
                )}

                {items.map((item, index) => (
                    <div className="csce-card" key={item._key}>
                        <div className="csce-card-top">
                            <span className="csce-card-numero">{index + 1}</span>
                            <DeleteOutlineIcon
                                className="csce-card-quitar"
                                onClick={() => onQuitarItem(item)}
                            />
                        </div>

                        <div className="lead-form-group">
                            <label>Paso del checklist</label>
                            <input
                                type="text"
                                value={item.nombreSubActividad}
                                onChange={(e) => onCambiarNombre(item._key, e.target.value)}
                                placeholder="Ej. Crear usuario de red"
                                maxLength={150}
                            />
                        </div>
                    </div>
                ))}

                <button type="button" className="csce-agregar-campo" onClick={onAgregarItem}>
                    <AddIcon /> Agregar paso
                </button>

                <div className="csce-footer">
                    <button
                        className="csce-footer-cancelar"
                        onClick={() => history.push(`/Permisos/Actividad`)}
                    >
                        Cancelar
                    </button>
                    <button className="csce-footer-guardar" onClick={onGuardar} disabled={guardando}>
                        {guardando ? "Guardando..." : "Guardar checklist"}
                    </button>
                </div>

            </div>

            <ButtonBack path={"/Permisos/Actividad"} />

        </PlantillaScreen>
    );
};

export default CatActividadSubActividadesScreen;
